<template>
  <div>
    <div class="d-flex">
      <v-spacer></v-spacer>
      <v-btn outlined class="mb-4 mr-2" @click="openLogDialog">
        <v-icon left>{{ icons.mdiHistory }}</v-icon>
        {{ $t("Näytä loki") }}
      </v-btn>
      <v-btn color="primary" class="mb-4 mr-4" @click="openAddDialog">
        <v-icon left>{{ icons.mdiPlus }}</v-icon>
        {{ $t("Lisää pääkäyttäjä") }}
      </v-btn>
    </div>

    <v-data-table :headers="tableColumns" :items="mainUsers" :loading="loading" :items-per-page="-1" hide-default-footer
      mobile-breakpoint="0" :no-data-text="$t('Ei pääkäyttäjiä')">
      <template #[`item.last_name`]="{ item }">
        <router-link :to="{ name: 'admin-user-form', params: { id: item.id } }"
          class="font-weight-medium text-decoration-none">
          {{ item.last_name }}
        </router-link>
      </template>

      <template #[`item.first_name`]="{ item }">
        <router-link :to="{ name: 'admin-user-form', params: { id: item.id } }"
          class="font-weight-medium text-decoration-none">
          {{ item.first_name }}
        </router-link>
      </template>

      <template #[`item.email`]="{ item }">
        <a v-if="item.email" :href="'mailto:' + item.email">{{ item.email }}</a>
      </template>

      <template #[`item.phone`]="{ item }">
        <a v-if="item.phone" :href="'tel:' + item.phone">{{ item.phone }}</a>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-btn small outlined color="error" :disabled="!canRemove(item)" :title="removeTitle(item)"
          @click="confirmRemove(item)">
          {{ $t("Poista") }}
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="addDialog" max-width="560px" persistent>
      <add-main-user-dialog v-if="addDialog" :existing-main-user-ids="mainUserIds" @cancel="addDialog = false"
        @promoted="onPromoted" />
    </v-dialog>

    <v-dialog v-model="logDialog" max-width="900px">
      <main-user-log-dialog v-if="logDialog" @close="logDialog = false" />
    </v-dialog>

    <v-dialog v-model="confirmDialog" max-width="440px">
      <v-card v-if="userToRemove">
        <v-card-title>{{ $t("Poista pääkäyttäjistä") }}</v-card-title>
        <v-card-text>
          {{
            $t(
              "confirm_remove_mainuser"
            ).replace(
              "{name}",
              `${userToRemove.first_name} ${userToRemove.last_name}`
            )
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false" :disabled="removing">
            {{ $t("Peruuta") }}
          </v-btn>
          <v-btn color="error" :loading="removing" @click="performRemove">
            {{ $t("Poista") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import { mdiPlus, mdiHistory } from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import adminApi from "@/api/admin";
import useAuth from "@/useAuth";
import addMainUserDialog from "./add_main_user_dialog.vue";
import mainUserLogDialog from "./main_user_log_dialog.vue";

export default {
  components: {
    addMainUserDialog,
    mainUserLogDialog,
  },
  setup() {
    const { t } = useTranslations();
    const { user } = useAuth();

    const mainUsers = ref([]);
    const loading = ref(false);
    const addDialog = ref(false);
    const logDialog = ref(false);
    const confirmDialog = ref(false);
    const userToRemove = ref(null);
    const removing = ref(false);

    const tableColumns = [
      { text: t("Sukunimi").toUpperCase(), value: "last_name", sortable: false },
      { text: t("Etunimi").toUpperCase(), value: "first_name", sortable: false },
      { text: t("Sähköposti").toUpperCase(), value: "email", sortable: false },
      { text: t("Puhelin").toUpperCase(), value: "phone", sortable: false },
      { text: "", value: "actions", sortable: false, align: "end", width: 64 },
    ];

    const mainUserIds = computed(() => mainUsers.value.map((u) => u.id));

    const fetchMainUsers = async () => {
      loading.value = true;
      const response = await adminApi.listMainUsers();
      if (response.ok) {
        mainUsers.value = response.data;
      }
      loading.value = false;
    };

    const canRemove = (item) => {
      if (item.id === user.value.id) return false;
      if (mainUsers.value.length <= 1) return false;
      return true;
    };

    const removeTitle = (item) => {
      if (item.id === user.value.id)
        return t("Et voi poistaa itseäsi pääkäyttäjistä");
      if (mainUsers.value.length <= 1)
        return t("Viimeistä pääkäyttäjää ei voi poistaa");
      return t("Poista pääkäyttäjistä");
    };

    const openAddDialog = () => {
      addDialog.value = true;
    };

    const openLogDialog = () => {
      logDialog.value = true;
    };

    const onPromoted = () => {
      addDialog.value = false;
      fetchMainUsers();
    };

    const confirmRemove = (item) => {
      userToRemove.value = item;
      confirmDialog.value = true;
    };

    const performRemove = async () => {
      if (!userToRemove.value) return;
      removing.value = true;
      const resp = await adminApi.demoteMainUser(userToRemove.value.id);
      removing.value = false;
      confirmDialog.value = false;
      userToRemove.value = null;
      if (resp.ok) {
        fetchMainUsers();
      }
    };

    onMounted(fetchMainUsers);

    return {
      mainUsers,
      mainUserIds,
      tableColumns,
      loading,
      addDialog,
      logDialog,
      confirmDialog,
      userToRemove,
      removing,
      canRemove,
      removeTitle,
      openAddDialog,
      openLogDialog,
      onPromoted,
      confirmRemove,
      performRemove,
      icons: {
        mdiPlus,
        mdiHistory,
      },
      t,
    };
  },
};
</script>
